import type { AutocaptureConfig, Mixpanel } from 'mixpanel-browser';
import { getPageMetadata } from '@/routes';
import { getAdCampaign } from '@/content/get-started';

declare const __WEBSITE_ANALYTICS__: {
  token: string;
  apiHost: string;
  environment: string;
};

type EventProperties = Record<string, string | number | boolean>;
type WebsiteEvent =
  | 'demo_opened'
  | 'demo_time_selected'
  | 'demo_booked'
  | 'app_chooser_opened'
  | 'open_app_clicked'
  | 'ad_use_case_selected'
  | 'ad_landing_viewed'
  | 'ad_demo_cta_clicked'
  | 'ad_detail_toggled'
  | 'docs_clicked';

const AUTOCAPTURE: AutocaptureConfig = {
  pageview: 'url-with-path',
  click: true,
  dead_click: true,
  rage_click: true,
  scroll: true,
  submit: true,
  input: false,
  capture_text_content: false,
  block_selectors: ['[data-analytics-private]'],
};
const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;
const LANDING_KEYS = [
  'landing_page',
  'landing_referrer',
  'landing_referring_domain',
  'landing_ad_variant',
  'landing_ad_initial_use_case',
  ...CAMPAIGN_KEYS.map((key) => `landing_${key}`),
];
type PageviewProperties = Record<string, string | number | boolean | null>;

let client: Mixpanel | undefined;
let loading: Promise<void> | undefined;
let started = false;
let landingProperties: Record<string, string | null> = {};
let pendingPageviews: PageviewProperties[] = [];
let stopBufferingPageviews: (() => void) | undefined;

function isWebsiteAnalyticsConfigured(): boolean {
  return (
    typeof __WEBSITE_ANALYTICS__ !== 'undefined' &&
    Boolean(__WEBSITE_ANALYTICS__.token)
  );
}

function canTrack(): boolean {
  return (
    typeof window !== 'undefined' &&
    isWebsiteAnalyticsConfigured() &&
    navigator.doNotTrack !== '1' &&
    !(navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl
  );
}

function pageProperties(): PageviewProperties {
  const campaign = getAdCampaign(window.location.pathname);
  return {
    site: 'marketing',
    environment: __WEBSITE_ANALYTICS__.environment,
    page_path: window.location.pathname,
    // History changes before React updates document.title. Resolve metadata
    // from the route so automatic navigation events carry the new title.
    page_title: getPageMetadata(window.location.pathname).title,
    // Explicit empty values also protect buffered non-ad events if the visitor
    // reaches a campaign page before the SDK finishes loading.
    ad_variant: campaign?.id ?? null,
    ad_initial_use_case: campaign?.initialUseCase ?? null,
  };
}

/** Capture pathname changes during SDK loading, then hand history back to the SDK. */
function bufferInitialPageviews(): () => void {
  let active = true;
  let previousPath: string | undefined;
  const capture = () => {
    if (!active || !canTrack() || previousPath === window.location.pathname)
      return;
    previousPath = window.location.pathname;
    const url = new URL(window.location.href);
    pendingPageviews.push({
      ...pageProperties(),
      $mp_autocapture: true,
      $current_url: url.href,
      current_page_title: getPageMetadata(url.pathname).title,
      current_domain: url.hostname,
      current_url_path: url.pathname,
      current_url_protocol: url.protocol,
      current_url_search: url.search,
      ...Object.fromEntries(
        CAMPAIGN_KEYS.map((key) => [key, url.searchParams.get(key)])
      ),
      time: Date.now() / 1000,
    });
  };
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;
  const pushState: History['pushState'] = function (this: History, ...args) {
    originalPushState.apply(this, args);
    capture();
  };
  const replaceState: History['replaceState'] = function (
    this: History,
    ...args
  ) {
    originalReplaceState.apply(this, args);
    capture();
  };
  window.history.pushState = pushState;
  window.history.replaceState = replaceState;
  window.addEventListener('popstate', capture);
  capture();
  return () => {
    active = false;
    window.removeEventListener('popstate', capture);
    if (window.history.pushState === pushState)
      window.history.pushState = originalPushState;
    if (window.history.replaceState === replaceState)
      window.history.replaceState = originalReplaceState;
  };
}

/** Reuse the SDK load while preserving the route history captured before it is ready. */
async function startClient(): Promise<void> {
  if (!canTrack() || client) return;
  if (loading) return loading;

  loading = import('mixpanel-browser')
    .then(({ default: mixpanel }) => {
      if (!canTrack()) return;
      stopBufferingPageviews?.();
      stopBufferingPageviews = undefined;
      mixpanel.init(
        __WEBSITE_ANALYTICS__.token,
        {
          api_host: __WEBSITE_ANALYTICS__.apiHost,
          persistence: 'localStorage',
          persistence_name: 'den_marketing',
          cross_subdomain_cookie: false,
          stop_utm_persistence: true,
          flags: false,
          remote_settings_mode: 'disabled',
          // Heatmaps also capture pageviews. The SDK owns navigation after startup.
          track_pageview: false,
          autocapture: AUTOCAPTURE,
          hooks: {
            before_track(this: Mixpanel, event, properties) {
              if (!canTrack()) return null;
              // Case: the SDK's first pageview drains the earlier route history.
              // Keep its current pageview and prepend only the earlier pages.
              const buffered =
                event === '$mp_web_page_view' ? pendingPageviews.splice(0) : [];
              const startupPage = buffered.pop();
              for (const page of buffered)
                this.track('$mp_web_page_view', page);
              return [
                event,
                {
                  ...pageProperties(),
                  ...properties,
                  ...startupPage,
                  ...landingProperties,
                  ...(event === '$mp_web_page_view'
                    ? {
                        current_page_title:
                          startupPage?.page_title ??
                          properties?.page_title ??
                          getPageMetadata(window.location.pathname).title,
                      }
                    : {}),
                },
              ];
            },
          },
          record_sessions_percent: 100,
          record_heatmap_data: true,
          // This SDK runs only on the public marketing site. Keep its public
          // copy visible in replays while masking visitor input and private UI.
          record_mask_all_text: false,
          record_mask_all_inputs: true,
          record_block_selector:
            'iframe, [data-analytics-private], .mp-sensitive, .mp-no-track',
          record_console: false,
          record_network: false,
          loaded(instance) {
            client = instance;
            // Remove older persisted attribution while keeping the visitor's identity.
            for (const key of LANDING_KEYS) instance.unregister(key);
            instance.register(
              {
                site: 'marketing',
                environment: __WEBSITE_ANALYTICS__.environment,
                ...landingProperties,
              },
              { persistent: false }
            );
          },
        },
        'marketing'
      );
    })
    .catch(() => {
      // Blocked or unavailable SDKs must not stop CTAs.
    })
    .finally(() => {
      stopBufferingPageviews?.();
      stopBufferingPageviews = undefined;
      pendingPageviews = [];
      loading = undefined;
    });
  return loading;
}

/** Called once from the browser entry, never during static rendering. */
export function initializeWebsiteAnalytics(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  // Keep the original landing campaign and referrer across route changes.
  const url = new URL(window.location.href);
  // Explicit empty values also override old stored fields on the first SDK event,
  // which is emitted before its loaded callback can remove persisted attribution.
  landingProperties = Object.fromEntries(
    LANDING_KEYS.map((key) => [key, null])
  );
  landingProperties.landing_page = url.origin + url.pathname;
  const campaign = getAdCampaign(url.pathname);
  if (campaign) {
    landingProperties.landing_ad_variant = campaign.id;
    landingProperties.landing_ad_initial_use_case = campaign.initialUseCase;
  }
  for (const key of CAMPAIGN_KEYS) {
    const value = url.searchParams.get(key);
    if (value) landingProperties[`landing_${key}`] = value;
  }
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      landingProperties.landing_referrer = referrer.origin + referrer.pathname;
      landingProperties.landing_referring_domain = referrer.hostname;
    } catch {
      // An unavailable referrer is normal for direct visits.
    }
  }

  if (canTrack()) {
    stopBufferingPageviews = bufferInitialPageviews();
    void startClient();
  }
}

export function trackWebsiteEvent(
  event: WebsiteEvent,
  properties: EventProperties = {}
): void {
  if (!canTrack()) return;
  // Snapshot the page before an outbound click or client-side navigation.
  const eventProperties = { ...properties, ...pageProperties() };
  if (client) {
    try {
      client.track(event, eventProperties);
    } catch {
      // Analytics failures must not interrupt the click handler.
    }
  } else {
    void startClient().then(() => {
      if (canTrack()) {
        try {
          client?.track(event, eventProperties);
        } catch {
          // The destination action has already continued independently.
        }
      }
    });
  }
}

export function trackDocsClick(href: string, location: string): void {
  if (!canTrack()) return;
  try {
    if (new URL(href).hostname === 'docs.mls.onchainden.com') {
      trackWebsiteEvent('docs_clicked', {
        destination: href,
        cta_location: location,
      });
    }
  } catch {
    // A malformed link must not interrupt navigation.
  }
}
